import _toConsumableArray from "/builder/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import { createContext, useContext, useRef, useLayoutEffect, useState, useEffect } from 'react';
import { jsx } from 'react/jsx-runtime';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';

function createContextHook(useHook) {
  var DefaultContext = /*#__PURE__*/createContext(null);
  var useHookContext = contextHookFactory(DefaultContext);

  var Provider = function Provider(props) {
    var context = useHook(props);
    return /*#__PURE__*/jsx(DefaultContext.Provider, {
      value: context,
      children: props.children
    });
  };

  return [Provider, useHookContext, DefaultContext];
}

function contextHookFactory(DefaultContext) {
  return function (selector, equalityCheck) {
    var context = useContext(DefaultContext);
    var previousContext = usePrevious(context);

    if (!context) {
      throw new Error('`useContextHook` must be placed inside the `Provider` returned by the `createContextState` method');
    }

    if (!selector) {
      return context;
    }

    if (typeof selector !== 'function') {
      throw new TypeError('invalid arguments passed to `useContextHook`. This hook must be called with zero arguments, a getter function or a path string.');
    }

    var value = selector(context);

    if (!previousContext || !equalityCheck) {
      return value;
    }

    var previousValue = selector(previousContext);
    return equalityCheck(previousValue, value) ? previousValue : value;
  };
}

function usePrevious(value) {
  var ref = useRef();
  useIsomorphicLayoutEffect(function () {
    ref.current = value;
  });
  return ref.current;
}

var useIsomorphicLayoutEffect = useLayoutEffect;
/**
 * Create a context and provider with built in setters and getters.
 *
 * ```tsx
 * import { createContextState } from 'create-context-state';
 *
 * interface Context {
 *   count: number;
 *   increment: () => void;
 *   decrement: () => void;
 *   reset: () => void;
 * }
 *
 * interface Props {
 *   defaultCount: number;
 * }
 *
 * const [CountProvider, useCount] = createContextState<Context, Props>(({ set, props }) => ({
 *   count: previousContext?.count ?? props.defaultCount,
 *   increment: () => set((state) => ({ count: state.count + 1 })),
 *   decrement: () => set((state) => ({ count: state.count - 1 })),
 *   reset: () => set({ count: props.defaultCount }),
 * }));
 *
 * const App = () => {
 *   return (
 *     <CountProvider>
 *       <Counter />
 *     </CountProvider>
 *   );
 * };
 *
 * const Counter = () => {
 *   const { count, increment, decrement, reset } = useCount();
 *
 *   return (
 *     <>
 *       <h1>{count}</h1>
 *       <button onClick={() => increment()}>+</button>
 *       <button onClick={() => decrement()}>-</button>
 *       <button onClick={() => reset()}>reset</button>
 *     </>
 *   );
 * };
 * ```
 *
 * @template Context - The type of the context that is returned from the
 * `useContext` hook.
 * @template Props - The optional props that are passed through to the
 * `Provider`.
 * @template State - Additional state which can be captured via hooks.
 *
 * @param creator - A function used to create the desired context.
 * @param hook - An optional hook which can be used to provide additional state
 * to use in the creator function. Using hooks which rely on context will
 * constrain the returned `Provider` function to only be used in scenarios where
 * the the context is available. Make sure to memoize any exotic values (objects
 * and arrays) returned from the hook or your code will infinitely render.
 */

function createContextState(creator, hook) {
  return createContextHook(function (props) {
    // Keep a ref to the context so that the `get` function can always be called
    // with the latest value.
    var contextRef = useRef(null);
    var setContextRef = useRef();
    var state = hook === null || hook === void 0 ? void 0 : hook(props);

    var _useState = useState(function () {
      return creator({
        get: createGet(contextRef),
        set: createSet(setContextRef),
        previousContext: undefined,
        props: props,
        state: state
      });
    }),
        _useState2 = _slicedToArray(_useState, 2),
        context = _useState2[0],
        setContext = _useState2[1];

    var dependencies = [].concat(_toConsumableArray(Object.values(props)), [state]); // Update the context whenever the props are updated. This is only ever
    // called when props are updated.

    useEffect(function () {
      // Don't update if no props or hooks are defined for this hook creator.
      if (dependencies.length === 0) {
        return;
      }

      setContext(function (previousContext) {
        return creator({
          get: createGet(contextRef),
          set: createSet(setContextRef),
          previousContext: previousContext,
          props: props,
          state: state
        });
      }); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies); // Keep the refs updated on each render.

    contextRef.current = context;
    setContextRef.current = setContext;
    return context;
  });
}
/**
 * Create a get function which is used to get the current state within the
 * `context` creator.
 */


function createGet(ref) {
  return function (pathOrSelector) {
    if (!ref.current) {
      throw new Error('`get` called outside of function scope. `get` can only be called within a function.');
    }

    if (!pathOrSelector) {
      return ref.current;
    }

    if (typeof pathOrSelector !== 'function') {
      throw new TypeError('Invalid arguments passed to `useContextHook`. The hook must be called with zero arguments, a getter function or a path string.');
    }

    return pathOrSelector(ref.current);
  };
}
/**
 * Create a `set` function which is used to set the context.
 */


function createSet(ref) {
  return function (partial) {
    if (!ref.current) {
      throw new Error('`set` called outside of function scope. `set` can only be called within a function.');
    }

    ref.current(function (context) {
      return _objectSpread(_objectSpread({}, context), typeof partial === 'function' ? partial(context) : partial);
    });
  };
}

export { createContextHook, createContextState };